import React, { useState } from 'react';
import {deleteUser} from "../auth";
import ConfirmationModal from "./ConfirmationModal";

interface User {
    id: number;
    email: string;
    role: string;
    isActive: boolean;
}

interface UserTableProps {
    users: User[];
    onEditUser: (role: User) => void;
    refreshUsers: () => void;  // Nouvelle prop pour rafraîchir la liste des rôles
}

const UserTable: React.FC<UserTableProps> = ({ users, onEditUser, refreshUsers }) => {
    const [filter, setFilter] = useState('');
    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilter(e.target.value);
    };

    const openModal = (user: User) => {
        setUserToDelete(user);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setUserToDelete(null);
    };

    const confirmDelete = async () => {
        if (userToDelete) {
            try {
                await deleteUser(userToDelete.id);
                refreshUsers();
            } catch (error) {
                console.error('Erreur lors de la suppression de l\'utilisateur:', error);
            } finally {
                closeModal();
            }
        }
    };

    const handleDeleteSelected = async () => {
        try {
            await Promise.all(selectedUsers.map(userId => deleteUser(userId)));
            refreshUsers();
            setSelectedUsers([]);
        } catch (error) {
            console.error('Erreur lors de la suppression des utilisateurs sélectionnés:', error);
        }
    };

    const filteredUsers = users.filter(
        (user) => user.email.includes(filter.toLowerCase())
    );

    const handleSelectUser = (userId: number) => {
        setSelectedUsers((prevSelected) =>
            prevSelected.includes(userId)
                ? prevSelected.filter((id) => id !== userId)
                : [...prevSelected, userId]
        );
    };

    return (
        <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="mb-4">
                <input
                    type="text"
                    placeholder="Filtrer par email"
                    className="p-2 border rounded-md w-full"
                    value={filter}
                    onChange={handleFilterChange}
                />
            </div>
            <table className="w-full table-auto">
                <thead>
                <tr>
                    <th className="px-4 py-2 text-center w-12">
                        <input
                            type="checkbox"
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setSelectedUsers(users.map((user) => user.id));
                                } else {
                                    setSelectedUsers([]);
                                }
                            }}
                            checked={selectedUsers.length === users.length}
                        />
                    </th>
                    <th className="px-4 py-2 text-center w-20">Identifiant</th>
                    <th className="px-4 py-2 text-center w-40">Email</th>
                    <th className="px-4 py-2 text-center w-60">Rôle</th>
                    <th className="px-4 py-2 text-center w-32">Actions</th>
                </tr>
                </thead>
                <tbody>
                {filteredUsers.map((user) => (
                    <tr key={user.id}>
                        <td className="px-4 py-2 text-center " >
                            <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={() => handleSelectUser(user.id)}
                            />
                        </td>
                        <td className="px-4 py-2 text-center ">{user.id}</td>
                        <td className="px-4 py-2 text-center ">{user.email}</td>
                        <td className="px-4 py-2 text-center ">{user.role}</td>
                        <td className="px-4 py-2 text-center " >
                            <button
                                onClick={() => onEditUser(user)}
                                className="bg-yellow-500 text-white px-2 py-1 rounded-md mr-2"
                                title={'Réinitialiser le mot de passe'}
                            >
                                <i className={'fas fa-key text-medium'}></i>
                            </button>
                            <button
                                onClick={() => onEditUser(user)}
                                className="bg-green-500 text-white px-2 py-1 rounded-md mr-2"
                                title={'Modifier les informations utilisateurs'}
                            >
                                <i className={'fas fa-pen-to-square text-medium'}></i>
                            </button>
                            <button
                                onClick={() => openModal(user)}
                                className="bg-red-500 text-white px-2 py-1 rounded-md"
                                title={'Supprimer l\'utilisateur'}
                            >
                                <i className={'fas fa-trash-can text-medium'}></i>
                            </button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
            {selectedUsers.length > 0 && (
                <div className="mt-4 text-left ">
                    <button
                        onClick={handleDeleteSelected}
                        className="bg-red-500 text-white px-4 py-2 rounded-md"
                        title={'Supprimer la sélection'}
                    >
                        <i className={'fas fa-trash-can text-xl'}></i>
                    </button>
                </div>
            )}
            <ConfirmationModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onConfirm={confirmDelete}
                message={`Êtes-vous sûr de vouloir supprimer l'utilisateur "${userToDelete?.email}" ?`}
            />
        </div>
    );
};


export default UserTable;
